// @ts-ignore — JS module without types
import { apiClient, apiEndpoint } from './api.services';
// @ts-ignore — JS module without types
import { API } from '../constants/api.constants';

const BASE = API.ASSET_LIBRARY_FILTER.BASE_PATH as string;

/** Standard Bombiix API response envelope (see docs/api-conventions.md). */
export interface ApiResponse<TData = unknown> {
  code?: number;
  message?: string;
  data?: TData;
}

export interface FilterValue {
  _id: string;
  label: string;
}

export interface FilterType {
  _id: string;
  filterKey: string;
  label: string;
  isActive: boolean;
  isSystem: boolean;
  values: FilterValue[];
}

/**
 * Fetches every asset-library filter type with its values
 * @returns API response containing the filter type list
 */
export const getFilters = async (): Promise<ApiResponse<FilterType[]>> => {
  return await apiClient.get(`${apiEndpoint}${BASE}`);
};

/**
 * Renames a filter type
 * @param filterTypeId - MongoDB ObjectId string of the filter type
 * @param label - New display label
 * @returns API response containing the updated filter type
 */
export const updateFilterTypeLabel = async (
  filterTypeId: string,
  label: string,
): Promise<ApiResponse<FilterType>> => {
  return await apiClient.patch(`${apiEndpoint}${BASE}/${filterTypeId}/label`, { label });
};

/**
 * Enables or disables a filter type
 * @param filterTypeId - MongoDB ObjectId string of the filter type
 * @param isActive - Whether the filter type should be active
 * @returns API response containing the updated filter type
 */
export const toggleFilterTypeActive = async (
  filterTypeId: string,
  isActive: boolean,
): Promise<ApiResponse<FilterType>> => {
  return await apiClient.patch(`${apiEndpoint}${BASE}/${filterTypeId}/toggle`, { isActive });
};

/**
 * Adds a value to a filter type
 * @param filterTypeId - MongoDB ObjectId string of the parent filter type
 * @param label - Display label for the new value
 * @returns API response containing the created filter value
 */
export const addFilterValue = async (
  filterTypeId: string,
  label: string,
): Promise<ApiResponse<FilterValue>> => {
  return await apiClient.post(`${apiEndpoint}${BASE}/${filterTypeId}/values`, { label });
};

/**
 * Renames an existing filter value
 * @param filterTypeId - MongoDB ObjectId string of the parent filter type
 * @param valueId - MongoDB ObjectId string of the value to rename
 * @param label - New display label
 * @returns API response containing the updated filter value
 */
export const editFilterValue = async (
  filterTypeId: string,
  valueId: string,
  label: string,
): Promise<ApiResponse<FilterValue>> => {
  return await apiClient.patch(`${apiEndpoint}${BASE}/${filterTypeId}/values/${valueId}`, { label });
};

/**
 * Deletes a filter value
 * @param filterTypeId - MongoDB ObjectId string of the parent filter type
 * @param valueId - MongoDB ObjectId string of the value to delete
 * @returns API response for the delete operation
 */
export const deleteFilterValue = async (
  filterTypeId: string,
  valueId: string,
): Promise<ApiResponse<null>> => {
  return await apiClient.delete(`${apiEndpoint}${BASE}/${filterTypeId}/values/${valueId}`);
};

/**
 * Creates a custom filter type
 * @param label - Display label of the new filter type
 * @returns API response containing the created filter type with an empty value list
 */
export const createFilterType = async (label: string): Promise<ApiResponse<FilterType>> => {
  return await apiClient.post(`${apiEndpoint}${BASE}`, { label });
};

/**
 * Deletes a custom filter type together with its values
 * @param filterTypeId - MongoDB ObjectId string of the filter type
 * @returns API response for the delete operation
 */
export const deleteFilterType = async (filterTypeId: string): Promise<ApiResponse<null>> => {
  return await apiClient.delete(`${apiEndpoint}${BASE}/${filterTypeId}`);
};
